/**
 * Enhanced Tree Types
 *
 * Extended types for comprehensive family tree visualization
 * with complete relationship support, caching, and change detection
 */

import { Person, Relationship, TreeNode, FamilyTree, TreeViewMode } from './index';

// ============================================
// ENHANCED RELATIONSHIP TYPES
// ============================================

/**
 * Biological relationship indicator
 */
export type BiologicalStatus = 'biological' | 'adoptive' | 'step' | 'foster' | 'guardian';

/**
 * Complete set of relationships for a person
 */
export interface CompleteRelationships {
  personId: string;
  parents: PersonParentInfo[];
  spouses: SpouseFamilyInfo[];
  children: PersonChildInfo[];
  siblings: PersonSiblingInfo[];
  adoptiveParents: PersonParentInfo[];
  stepParents: PersonParentInfo[];
  halfSiblings: PersonSiblingInfo[];
  stepSiblings: PersonSiblingInfo[];
}

/**
 * Parent information with biological status
 */
export interface PersonParentInfo {
  person: Person;
  relationship: Relationship;
  biologicalStatus: BiologicalStatus;
  isFather: boolean;
  isMother: boolean;
}

/**
 * Spouse family group (spouse + shared children)
 */
export interface SpouseFamilyInfo {
  spouse: Person;
  relationship: Relationship;
  marriageOrder: number;
  isCurrent: boolean;
  sharedChildren: PersonChildInfo[];
}

/**
 * Child information with biological status
 */
export interface PersonChildInfo {
  person: Person;
  relationship: Relationship;
  biologicalStatus: BiologicalStatus;
  otherParent?: Person; // The other parent (if known)
}

/**
 * Sibling information with type
 */
export interface PersonSiblingInfo {
  person: Person;
  relationship: Relationship;
  siblingType: 'full' | 'half' | 'step' | 'adoptive';
  sharedParents: Person[]; // Parents shared with this sibling
}

// ============================================
// ENHANCED TREE TYPES
// ============================================

/**
 * Enhanced tree node with complete relationship information
 */
export interface EnhancedTreeNode extends TreeNode {
  /** Biological status of parent-child relationship */
  biologicalStatus?: BiologicalStatus;

  /** Enhanced spouse information with shared children */
  enhancedSpouses?: SpouseFamilyInfo[];

  /** Siblings at this generation level */
  siblings?: PersonSiblingInfo[];

  /** Relationship metadata */
  relationshipMetadata?: RelationshipMetadata;
}

/**
 * Metadata about relationships for rendering
 */
export interface RelationshipMetadata {
  hasMultipleMarriages: boolean;
  hasAdoptedChildren: boolean;
  hasStepChildren: boolean;
  hasSiblings: boolean;
  totalSpouses: number;
  totalChildren: number;
  totalSiblings: number;
}

/**
 * Enhanced family tree with caching metadata
 */
export interface EnhancedFamilyTree extends FamilyTree {
  /** Enhanced root node */
  root: EnhancedTreeNode;

  /** Cache metadata */
  cacheMetadata: CacheMetadata;

  /** Relationship statistics */
  relationshipStats: RelationshipStatistics;
}

/**
 * Cache metadata for tracking freshness
 */
export interface CacheMetadata {
  /** When this tree was built */
  builtAt: Date;

  /** Data source timestamp */
  dataFetchedAt: Date;

  /** Cache version for invalidation */
  version: string;

  /** Whether data came from cache */
  fromCache: boolean;

  /** Number of Firestore queries made */
  queriesExecuted: number;
}

/**
 * Statistics about relationships in the tree
 */
export interface RelationshipStatistics {
  totalPersons: number;
  totalRelationships: number;
  biologicalRelationships: number;
  adoptiveRelationships: number;
  stepRelationships: number;
  marriages: number;
  divorces: number;
  siblingGroups: number;
}

// ============================================
// CACHE TYPES
// ============================================

/**
 * In-memory cache structure
 */
export interface TreeDataCache {
  /** All persons indexed by ID */
  persons: Map<string, Person>;

  /** All relationships indexed by person ID */
  relationshipsByPerson: Map<string, Relationship[]>;

  /** All relationships (flat list) */
  allRelationships: Relationship[];

  /** Built trees indexed by cache key */
  trees: Map<string, EnhancedFamilyTree>;

  /** When cache was last populated */
  lastFetched: Date;

  /** Cache version */
  version: string;
}

/**
 * localStorage persisted cache
 */
export interface PersistedTreeCache {
  persons: Person[];
  relationships: Relationship[];
  version: string;
  lastFetched: string; // ISO date string
  expiresAt: string; // ISO date string
}

/**
 * Cache configuration
 */
export interface CacheConfig {
  /** Enable in-memory caching */
  enableMemoryCache: boolean;

  /** Enable localStorage persistence */
  enableLocalStorage: boolean;

  /** TTL in milliseconds (default: 5 minutes) */
  ttlMs: number;

  /** Maximum cache size (persons) */
  maxCacheSize: number;

  /** Enable change detection listeners */
  enableChangeDetection: boolean;
}

// ============================================
// CHANGE DETECTION TYPES
// ============================================

/**
 * Change event emitted by detection service
 */
export interface TreeChangeEvent {
  /** Type of entity that changed */
  entityType: 'person' | 'relationship' | 'photo' | 'lifeEvent';

  /** Change operation */
  changeType: 'create' | 'update' | 'delete';

  /** Entity ID that changed */
  entityId: string;

  /** Timestamp of change */
  timestamp: Date;

  /** User who made the change (if available) */
  userId?: string;

  /** Affected person IDs (for cache invalidation) */
  affectedPersonIds: string[];
}

/**
 * Change listener callback
 */
export type ChangeCallback = (event: TreeChangeEvent) => void;

/**
 * Unsubscribe function for change listeners
 */
export type Unsubscribe = () => void;

// ============================================
// SERVICE OPTIONS
// ============================================

/**
 * Options for building enhanced trees
 */
export interface EnhancedTreeBuildOptions {
  /** Root person ID */
  rootPersonId: string;

  /** Tree view mode */
  viewMode: TreeViewMode;

  /** Maximum depth to traverse */
  maxDepth?: number;

  /** Include siblings */
  includeSiblings?: boolean;

  /** Include adopted/step relationships */
  includeNonBiological?: boolean;

  /** Include extended family (aunts, uncles, cousins) */
  includeExtendedFamily?: boolean;

  /** Force refresh (bypass cache) */
  forceRefresh?: boolean;

  /** Cache configuration */
  cacheConfig?: Partial<CacheConfig>;
}

/**
 * Tree cache key generator
 */
export interface TreeCacheKey {
  rootPersonId: string;
  viewMode: TreeViewMode;
  maxDepth: number;
  includeSiblings: boolean;
  includeNonBiological: boolean;

  /** Generate cache key string */
  toString(): string;
}

// ============================================
// DEFAULT CONFIGURATIONS
// ============================================

/**
 * Default cache configuration
 */
export const DEFAULT_CACHE_CONFIG: CacheConfig = {
  enableMemoryCache: true,
  enableLocalStorage: true,
  ttlMs: 5 * 60 * 1000, // 5 minutes
  maxCacheSize: 10000, // 10k persons
  enableChangeDetection: true,
};

/**
 * Default enhanced tree build options
 */
export const DEFAULT_ENHANCED_BUILD_OPTIONS: Partial<EnhancedTreeBuildOptions> = {
  maxDepth: 15,
  includeSiblings: true,
  includeNonBiological: true,
  includeExtendedFamily: false,
  forceRefresh: false,
  cacheConfig: DEFAULT_CACHE_CONFIG,
};
